import { getFontCss, styleConstants } from '@paywithextend/components'
import Button from 'ext-ui/components/Button/Button'
import React, { useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { Route, Switch } from 'react-router'
import ExpenseRequirementsAdditionalFields from 'src/app/ExpenseRequirementsAdditionalFields/ExpenseRequirementsAdditionalFields'
import LayoutPage from 'src/component-layout/LayoutPage/LayoutPage'
import CreateExpenseCategoryModal from 'src/component-modal/CreateExpenseCategoryModal/CreateExpenseCategoryModal'
import ExpenseCategoriesList from 'src/components/ExpenseCategoriesList/ExpenseCategoriesList'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import { SubnavOption } from 'src/components/Subnav/Subnav'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { useQueryParamArrayHandler } from 'src/hooks'
import { useExtendLocation } from 'src/hooks/locationHooks'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import { AppLocation } from 'src/navigation/appLocations'
import {
  expenseCategoriesAccountingRouteProps,
  expenseCategoriesAdditionalFieldsRouteProps,
} from 'src/navigation/routeProps'
import {
  expenseCategoriesAccountingUrl,
  expenseCategoriesAdditionalFieldsUrl,
} from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { selectOrganizationIdFromRoute } from 'src/redux/selectors/data/organizations.selectors'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import { selectLoadedFromRemoteConfigState } from 'src/redux/selectors/data/remoteConfig.selectors'
import {
  selectCalculatedBackgroundLocation,
  selectQueryParams,
} from 'src/redux/selectors/router.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

const ExpenseCategories: React.FC = () => {
  const queryParams = useSelector(selectQueryParams)
  const appName = useSelector(selectAppName)
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )

  const translate = useTranslate()
  const loadedRemoteConfig = useSelector(selectLoadedFromRemoteConfigState)
  const currentUser = useCurrentUser()
  const organizationId = useSelector(selectOrganizationIdFromRoute)
  const extendLocation = useExtendLocation()

  const activeIntegration = useSelector(selectActiveOrgIntegration)
  const {
    EDIT_EXPENSE_CATEGORIES: hasEditExpCatPermissions,
    EDIT_ADDITIONAL_FIELDS: hasEditAdditionalFieldsPermissions,
    VIEW_ADDITIONAL_FIELDS: hasViewAdditionalFieldsPermissions,
  } = useQueryUserFeatures([
    UserFeature.EDIT_EXPENSE_CATEGORIES,
    UserFeature.EDIT_ADDITIONAL_FIELDS,
    UserFeature.VIEW_ADDITIONAL_FIELDS,
  ]).data || {}
  const canAddField =
    hasEditAdditionalFieldsPermissions && hasViewAdditionalFieldsPermissions

  const [isCreateExpCatModalOpen, setIsCreateExpCatModalOpen] = useState(false)
  const [addingNewField, setAddingNewField] = useState<boolean>(false)

  const expenseCategoriesSubnavOptions = useMemo(
    (): SubnavOption[] => [
      {
        label: translate('Accounting Categories'),
        to: expenseCategoriesAccountingUrl(
          organizationId !== undefined ? organizationId : '',
        ),
        isActive:
          extendLocation.location ===
          AppLocation.OrganizationSettingsExpenseCategories,
        ariaLabelSource: translate('Accounting Categories'),
        'data-testid': 'subnavExpenseCategories',
      },
      {
        label: (
          <span
            css={css`
              display: inline-flex;
              align-items: center;
              gap: 6px;
            `}
          >
            {translate('Custom Fields')}
            <span
              css={css`
                display: inline-flex;
                align-items: center;
                background-color: rgb(103, 36, 249, 0.15);
                border-radius: 5px;
                padding: 2px 6px;
                ${getFontCss(
                  12,
                  styleConstants.colors.purple_dark,
                  styleConstants.fontFamilies.medium,
                )};
              `}
            >
              {'★ New'}
            </span>
          </span>
        ),
        to: expenseCategoriesAdditionalFieldsUrl(
          organizationId !== undefined ? organizationId : '',
        ),
        isActive:
          extendLocation.location ===
          AppLocation.OrganizationSettingsExpenseCategoriesAdditionalFields,
        ariaLabelSource: translate('Custom Fields'),
        'data-testid': 'subnavExpenseCategoriesAdditionalFields',
      },
    ],
    [extendLocation, organizationId, translate],
  )

  if (!loadedRemoteConfig) {
    return <LoadingBackgroundPage />
  }

  if (!currentUser?.hasOrgBookkeeperPermissions) {
    return <NotFoundBackgroundPage />
  }

  return (
    <>
      <Helmet>
        <title>{`Expense Categories | ${appName}`}</title>
        <meta
          name="description"
          content={`Expense Categories page of ${appName}`}
        />
      </Helmet>
      <LayoutPage
        subnavOptions={expenseCategoriesSubnavOptions}
        title={<span>{translate('Expense Categories')}</span>}
        actions={
          <>
            {extendLocation.location ===
              AppLocation.OrganizationSettingsExpenseCategories &&
              activeIntegration === undefined &&
              hasEditExpCatPermissions && (
                <Button
                  data-testid="new-category-btn"
                  variant={'primary'}
                  dropShadow={false}
                  onClick={() => setIsCreateExpCatModalOpen(true)}
                >
                  {translate('New Category +')}
                </Button>
              )}
            {extendLocation.location ===
              AppLocation.OrganizationSettingsExpenseCategoriesAdditionalFields &&
              canAddField && (
                <Button
                  data-testid="new-category-btn"
                  variant={'primary'}
                  dropShadow={false}
                  onClick={() => {
                    setAddingNewField(true)
                  }}
                  disabled={addingNewField}
                >
                  {translate('New Field +')}
                </Button>
              )}
          </>
        }
      >
        <Switch location={calculatedBackgroundLocation}>
          <Route {...expenseCategoriesAdditionalFieldsRouteProps}>
            <ExpenseRequirementsAdditionalFields
              addingNewField={addingNewField}
              setAddingNewField={setAddingNewField}
            />
          </Route>
          <Route {...expenseCategoriesAccountingRouteProps}>
            <ExpenseCategoriesList
              initialFilterValues={{
                search: (
                  useQueryParamArrayHandler(queryParams.search) as string[]
                )?.[0],
                status: useQueryParamArrayHandler(
                  queryParams.status,
                ) as string[],
                requiredForVirtualCards: useQueryParamArrayHandler(
                  queryParams.requiredForVirtualCards,
                ) as string[],
                requiredForTransactions: useQueryParamArrayHandler(
                  queryParams.requiredForTransactions,
                ) as string[],
              }}
            />
          </Route>
          <Route path={'/'}>
            <NotFoundBackgroundPageContent />
          </Route>
        </Switch>
        <CreateExpenseCategoryModal
          isOpen={isCreateExpCatModalOpen}
          onClose={() => setIsCreateExpCatModalOpen(false)}
        />
      </LayoutPage>
    </>
  )
}

export default ExpenseCategories
